/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Autocomplete } from '@mui/material';
import { useFormik } from 'formik';
import { IAction } from 'src/js/models/IshAction';
import * as yup from 'yup';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { makeAppStyles } from '../../../styles/makeStyles';
import CustomTextField from '../../common/fields/TextField';
import Navigation from '../Navigations';
import { countries, countriesTimeZone } from '../../../utils';
import { addEventListenerWithDeps } from '../../../hooks/addEventListnerWithDeps';
import { State } from '../../../models/State';
import { createCollege, setOrganisationFormValues } from '../../../redux/actions/College';

const useStyles = makeAppStyles()((theme: any) => ({
  textFieldWrapper: {
    minHeight: '66px',
  },
  subTitle: {
    marginBottom: 30,
  },
  sectionTitle: {
    fontFamily: theme.typography.fontFamily,
    fontSize: 22
  },
  groupOrganization: {
    marginBottom: 50,
  },
  groupOrganizationDetails: {
    marginBottom: 20,
  },
}));

const validationSchema = yup.object({
  organisationName: yup.string().required('Required'),
  address: yup.string().required('Required'),
  suburb: yup.string().required('Required'),
  state: yup.string().required('Required'),
  postcode: yup.string().required('Required'),
  country: yup.string().required('Required').nullable(),
  timeZone: yup.string().required('Required').nullable(),
});

const OrganisationForm = (props: any) => {
  const { classes } = useStyles();
  const [validState, setValidState] = useState(false);
  const {
    activeStep,
    steps,
    handleBack,
    handleNext,
    organisationForm,
    setOrganisationFormValues,
    createCollege,
    collegeKey,
    webSiteTemplate,
    contactForm,
    collegeWasCreated,
    currency
  } = props;

  useEffect(() => {
    if (collegeWasCreated) {
      handleNext();
    } else {
      if (!values.timeZone) {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        values.timeZone = countriesTimeZone.find(tz => tz === timeZone) ? timeZone : "";
      }
    }
  }, [collegeWasCreated]);

  const {
    handleChange, values, errors, setFieldValue, isValid, touched, dirty, handleBlur
  } = useFormik({
    initialValues: organisationForm,
    validationSchema,
    onSubmit: () => {},
  });

  const handleBackCustom = () => {
    setOrganisationFormValues(values);
    handleBack();
  };

  const handleNextCustom = () => {
    setOrganisationFormValues(values);
    createCollege({
      collegeKey,
      webSiteTemplate,
      currency,
      ...contactForm,
      ...values,
    });
  };

  const keyPressHandler = useCallback((e) => {
    if (e.keyCode === 13 && !((dirty && !isValid) || (!dirty && !validState))) {
      handleNextCustom();
    }
  }, [dirty, isValid, validState]);

  addEventListenerWithDeps([keyPressHandler], keyPressHandler);

  useEffect(() => {
    validationSchema.validate(organisationForm).then(() => {
      setValidState(true);
    }).catch(() => {
      setValidState(false);
    });
  }, organisationForm);

  return (
    <form>
      <Typography variant="h4" component="h4" className="coloredHeaderText" color="primary" gutterBottom>
        Enter your organisation details
      </Typography>
      <Typography variant="subtitle1" gutterBottom className={classes.subTitle}>
        Tell us a bit about your business
      </Typography>

      <Typography variant="h5" component="h5" className={classes.sectionTitle} color="initial" gutterBottom>
        Your Organisation
      </Typography>
      <Grid container spacing={5} className={classes.groupOrganization}>
        <Grid item xs={12} sm={6}>
          <div className={classes.textFieldWrapper}>
            <CustomTextField
              placeholder="Organisation Name"
              label="Organisation Name"
              id="organisationName"
              autoFocus
              onChange={handleChange}
              value={values.organisationName}
              error={Boolean(touched.organisationName && errors.organisationName)}
              helperText={touched.organisationName && errors.organisationName}
              onBlur={handleBlur}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className={classes.textFieldWrapper}>
            <CustomTextField
              placeholder="Company No. / ABN"
              label="Company No. / ABN"
              id="abn"
              onChange={handleChange}
              value={values.abn}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className={classes.textFieldWrapper}>
            <CustomTextField
              placeholder="Trading Name"
              label="Trading Name"
              id="tradingName"
              onChange={handleChange}
              value={values.tradingName}
              helperText="(if different to organisation name)"
            />
          </div>
        </Grid>
      </Grid>

      <Typography variant="h5" component="h5" className={classes.sectionTitle} color="initial" gutterBottom>
        Your Organisation's Details
      </Typography>
      <Grid container spacing={5} className={classes.groupOrganizationDetails}>
        <Grid item xs={12} sm={6}>
          <div className={classes.textFieldWrapper}>
            <CustomTextField
              placeholder="Street Address"
              label="Street Address"
              id="address"
              onChange={handleChange}
              value={values.address}
              error={Boolean(touched.address && errors.address)}
              helperText={touched.address && errors.address}
              onBlur={handleBlur}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className={classes.textFieldWrapper}>
            <CustomTextField
              placeholder="City or Suburb"
              label="City or Suburb"
              id="suburb"
              onChange={handleChange}
              value={values.suburb}
              error={Boolean(touched.suburb && errors.suburb)}
              helperText={touched.suburb && errors.suburb}
              onBlur={handleBlur}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className={classes.textFieldWrapper}>
            <CustomTextField
              placeholder="State"
              label="State"
              id="state"
              onChange={handleChange}
              value={values.state}
              error={Boolean(touched.state && errors.state)}
              helperText={touched.state && errors.state}
              onBlur={handleBlur}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className={classes.textFieldWrapper}>
            <CustomTextField
              placeholder="Postcode / ZIP"
              label="Postcode / ZIP"
              id="postcode"
              onChange={handleChange}
              value={values.postcode}
              error={Boolean(touched.postcode && errors.postcode)}
              helperText={touched.postcode && errors.postcode}
              onBlur={handleBlur}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className={classes.textFieldWrapper}>
            <Autocomplete
              id="country"
              options={countries}
              getOptionLabel={(option: string) => option}
              onChange={(_, value) => setFieldValue('country', value)}
              value={values.country}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  label="Country"
                  margin="normal"
                  error={Boolean(touched.country && errors.country)}
                  helperText={touched.country && errors.country}
                  onBlur={handleBlur}
                />
              )}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className={classes.textFieldWrapper}>
            <Autocomplete
              id="timeZone"
              options={countriesTimeZone}
              getOptionLabel={(option: string) => option}
              onChange={(_, value) => setFieldValue('timeZone', value)}
              value={values.timeZone}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  label="Time zone"
                  margin="normal"
                  error={Boolean(touched.timeZone && errors.timeZone)}
                  helperText={touched.timeZone && errors.timeZone}
                  onBlur={handleBlur}
                />
              )}
            />
          </div>
        </Grid>
        <Grid item xs={12}>
          <Navigation
            activeStep={activeStep}
            steps={steps}
            handleBack={handleBackCustom}
            handleNext={handleNextCustom}
            disabled={(dirty && !isValid) || (!dirty && !validState)}
          />
        </Grid>
      </Grid>
    </form>
  );
};

const mapStateToProps = (state: State) => ({
  collegeWasCreated: state.college.collegeWasCreated,
  collegeKey: state.college.collegeKey,
  webSiteTemplate: state.college.webSiteTemplate,
  contactForm: state.form.contactForm,
  organisationForm: state.form.organisationForm,
  currency: state.college.currency
});

const mapDispatchToProps = (dispatch: Dispatch<IAction>) => ({
  createCollege: (data) => dispatch(createCollege(data)),
  setOrganisationFormValues: (organisationFormData) => dispatch(setOrganisationFormValues(organisationFormData)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrganisationForm);
